import { Data } from "react-csv/lib/core";
import { IImportStatus } from "../../Typings/Imports";
import { Box, Button, CircularProgress, Link, SxProps, Theme } from "@mui/material";
import { ImportStatus } from "../../Enums/ImportStatus";
import { CSVLink } from "react-csv";
import { FileDownload } from "@mui/icons-material";
import { useEffect, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useImportStatusStore } from "../../State/Stores/ImportStatusStore";
import { useShallow } from "zustand/react/shallow";
import { useImportCandidateCSVMutation, useImportStatus } from "../../Queries/ImportQueries";

const defaultImportSx = {
  display: "flex",
  gap: "1ch",
  alignItems: "center",
} as SxProps<Theme>;

function getImportStatusSx(status: IImportStatus) {
  return {
    ...defaultImportSx,
    ...(status.status === ImportStatus.Created
      ? {
          position: "relative",
          overflow: "hidden",
          paddingInlineEnd: "2em",
        }
      : {}),
  } as SxProps<Theme>;
}

function getImportStatusDataTest(status: IImportStatus) {
  switch (status.status) {
    case ImportStatus.Created:
      return `import-status-created-${status.id}`;
    case ImportStatus.InProgress:
      return `import-status-in-progress-${status.id}`;
    case ImportStatus.Completed:
      return `import-status-completed-${status.id}`;
    case ImportStatus.CompletedWithFailures:
      return `import-status-completed-with-failures-${status.id}`;
    case ImportStatus.CompletedWithConflicts:
      return `import-status-completed-with-conflicts-${status.id}`;
    case ImportStatus.Failed:
      return `import-status-failed-${status.id}`;
    default:
      return "";
  }
}

/**
 * Show the import status text or notification based on the page and current import status
 *
 * If the notification is shown when the page renders, we switch to showing the text
 * @returns
 */
export function DisplayImportStatus() {
  const {
    statusId,
    showImportStatusText,
    showImportStatusNotification,
    isValidId,
    setShowImportStatusText,
    setShowImportStatusNotification,
  } = useImportStatusStore(
    useShallow((state) => ({
      statusId: state.statusId,
      showImportStatusText: state.showImportStatusText,
      showImportStatusNotification: state.showImportStatusNotification,
      isValidId: state.isValidId,
      setShowImportStatusText: state.setShowImportStatusText,
      setShowImportStatusNotification: state.setShowImportStatusNotification,
    })),
  );
  const { data: status } = useImportStatus(statusId ?? "");
  const { isLoading } = useImportCandidateCSVMutation();
  const isSuccessImport = useMemo(
    () =>
      status?.status === ImportStatus.Completed ||
      status?.status === ImportStatus.CompletedWithConflicts ||
      status?.status === ImportStatus.CompletedWithFailures,
    [status],
  );
  const isFailedImport = useMemo(() => status?.status === ImportStatus.Failed, [status]);

  useEffect(() => {
    if (showImportStatusNotification) {
      setShowImportStatusText(true);
      setShowImportStatusNotification(false);
    } else {
      setShowImportStatusText(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showImportStatusNotification]);

  useEffect(() => {
    let setStatusTimeout: ReturnType<typeof setTimeout>;

    if (showImportStatusText) {
      if (isValidId() && isSuccessImport) {
        setStatusTimeout = setTimeout(() => {
          setShowImportStatusText(false);
        }, 3000);
      } else if (isFailedImport) {
        setStatusTimeout = setTimeout(() => {
          setShowImportStatusText(false);
        }, 6000);
      }
    }

    return () => {
      clearTimeout(setStatusTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusId, isSuccessImport, isFailedImport, showImportStatusText, status?.status, status?.errorCount]);

  if (isLoading) {
    return (
      <Box sx={defaultImportSx} data-test="import-status-loading">
        File submitted, importing candidates...
        <CircularProgress size={20} color="inherit" />
      </Box>
    );
  }
  return showImportStatusText && status?.status ? (
    <Box sx={getImportStatusSx(status)} data-test={getImportStatusDataTest(status)}>
      <ImportStatusText status={status} />
    </Box>
  ) : null;
}

function ImportStatusText({ status }: { status: IImportStatus }) {
  const progress = status.totalRows ? (status.totalSuccesses / status.totalRows) * 100 : 0;
  switch (status.status) {
    case ImportStatus.Created:
      return (
        <>
          File submitted, importing candidates...
          <Box sx={{ display: "flex" }}>
            <CircularProgress size={20} color="inherit" />
          </Box>
        </>
      );
    case ImportStatus.InProgress:
      return (
        <>
          Import processing...{" "}
          <Box>
            <CircularProgress value={progress} size={20} color="primary" variant="determinate" />
          </Box>
        </>
      );
    case ImportStatus.Completed:
    case ImportStatus.CompletedWithFailures:
    case ImportStatus.CompletedWithConflicts:
      return <CompletedImportDisplay status={status} />;
    case ImportStatus.Failed:
      return (
        <>
          Import failed
          <br />
          <Button variant="text" component={RouterLink} to={`/candidates/imports?importId=${status.id}`}>
            View Import Details
          </Button>
        </>
      );
    default:
      return null;
  }
}

function CompletedImportDisplay({ status }: { status: IImportStatus }) {
  let message = "Import complete";
  if (status.totalSuccesses > 0) {
    message += `, ${status.totalSuccesses} candidates imported`;
  }
  if (status.pendingConflicts > 0) {
    message += `, ${status.pendingConflicts} conflicts`;
  }
  return (
    <>
      {message}
      <br />
      <Link color="inherit" component={RouterLink} to={`/candidates/imports?importId=${status.id}`}>
        View Import Details
      </Link>
    </>
  );
}

// function FailedCandidateCSVDownload({
//   importId,
//   data,
// }: {
//   importId: string;
//   data: string | Data | (() => string | Data);
// }) {
//   const { data } = useImportErrors(importId);
//   const dataResult = typeof data === "function" ? data() : data;
//   const displayCount = Array.isArray(dataResult)
//     ? dataResult.length
//     : typeof dataResult === "string"
//       ? dataResult.split("\n").length
//       : 0;
//   return (
//     <CSVLink
//       data-test="download-failed-candidates"
//       headers={failedCandidateHeaders}
//       data={data}
//       style={{ padding: "0", float: "none", color: "white", textDecoration: "none" }}
//       filename={`failed-imports-${importId}-${new Date().toJSON().slice(0, 10)}`}
//     >
//       <Button variant="text" color="inherit" sx={(theme) => ({ display: "flex", gap: theme.spacing(1) })}>
//         <FileDownload color="inherit" /> Download {displayCount} Failed Imports
//       </Button>
//     </CSVLink>
//   );
// }

const failedCandidateHeaders = [
  "firstname",
  "lastname",
  "email",
  "phone",
  "company",
  "title",
  "location",
  "errorMessage",
];
